import GL from "_src/engine/gl";
import FrameBuffer from "_src/engine/framebuffer";

import {attachmentId, blendFactor, pixelBufferType, primitiveType, RBIntFormat} from "_src/engine/constants";

import Pass from "_src/render_graph/pass";
import resourceManager from "_src/render_graph/resource_manager";
import Camera from "_src/engine/camera";
import SurfelsNormalizationShader from "_src/shaders/surfels_normalization/surfels_normalization"
import objFactory from "_src/engine/factories/object";
import primitiveFactory from "_src/engine/factories/primitive";
import CopyShader from "_src/shaders/copy/copy";
import compat from "_src/engine/compat";
import config from "_src/config";

export default class MainPass extends Pass {

  constructor(attachmentsStorage, camera) {

    super(attachmentsStorage);
    this.enableColorTexture = false;
    this.enableTriangleIDTexture = false;
    this.enableObjectIDTexture = true;
    this.enableGeneralPurposeTexture = false;
    this.init(camera);

  }

  init(camera) {
    const screenCamera = new Camera();

    //clear scenes
    this._addScene(camera);
    this._addScene(camera);

    const workspaceScene = this._addScene(camera);
    const surfelsDepthScene = this._addScene(camera);
    const surfelsAdditiveScene = this._addScene(camera);
    const surfelsNormalizationScene = this._addScene(screenCamera);
    const postBlendingScene = this._addScene(camera);
    const postBlendingCopyScene = this._addScene(screenCamera);
    const cadsAdditionalScene = this._addScene(camera);

    const objectIDScene = this._addScene(camera);
    const generalPurposeScene = this._addScene(camera);

    workspaceScene.clearedColor = false;

    objectIDScene.clearedColor = false;
    objectIDScene.clearedStencil = false;

    generalPurposeScene.clearedColor = false;
    generalPurposeScene.clearedStencil = false;

    surfelsDepthScene.clearedColor = false;
    surfelsDepthScene.clearedDepth = false;
    surfelsDepthScene.clearedStencil = false;
    surfelsDepthScene.isDepthPrepass = true;

    surfelsAdditiveScene.clearedColor = false;
    surfelsAdditiveScene.clearedDepth = false;
    surfelsAdditiveScene.clearedStencil = false;

    surfelsNormalizationScene.clearedColor = false;
    const surfelsNormalizationShader = new SurfelsNormalizationShader({transparent : true});
    surfelsNormalizationShader.setSourceBlendFactor(blendFactor.ONE);
    const normalizationScreenPlane = objFactory.createDrawable(surfelsNormalizationShader);
    normalizationScreenPlane.addGeometryChunk(primitiveFactory.create(primitiveType.SCREEN_QUAD));
    surfelsNormalizationScene.add(normalizationScreenPlane);

    postBlendingScene.clearedColor = false;
    postBlendingScene.clearedDepth = false;
    postBlendingScene.clearedStencil = false;
    postBlendingScene.isPostBlending = true;

    postBlendingCopyScene.clearedColor = false;
    const copyShader = new CopyShader({transparent : true});
    copyShader.setSourceBlendFactor(blendFactor.SRC_ALPHA);
    copyShader.setDestBlendFactor(blendFactor.ONE_MINUS_SRC_ALPHA);
    const copyScreenPlane = objFactory.createDrawable(copyShader);
    copyScreenPlane.addGeometryChunk(primitiveFactory.create(primitiveType.SCREEN_QUAD));
    postBlendingCopyScene.add(copyScreenPlane);

    cadsAdditionalScene.clearedColor = false;
    cadsAdditionalScene.isObjectIDPass = true;
  }

  update() {

    const clearScene1 = this.getScenes()[0];
    const clearScene2 = this.getScenes()[1];
    const workspaceScene = this.getScenes()[2];
    const surfelsDepthScene = this.getScenes()[3];
    const surfelsAdditiveScene = this.getScenes()[4];
    const surfelsNormalizationScene = this.getScenes()[5];
    const postBlendingScene = this.getScenes()[6];
    const postBlendingCopyScene = this.getScenes()[7];
    const cadsAdditionalScene = this.getScenes()[8];
    const objectIDScene = this.getScenes()[9];
    const generalPurposeScene = this.getScenes()[10];

    const width = workspaceScene.camera.viewport[2];
    const height = workspaceScene.camera.viewport[3];

    const { clearFrameBuffer1, clearFrameBuffer2, workspaceFrameBuffer, objectIDFrameBuffer, generalPurposeFrameBuffer,
      surfelsDepthFrameBuffer, postBlendingFrameBuffer, resolveFrameBuffer, surfelsAdditiveFrameBuffer,
      cadsAdditionalFrameBuffer } = this.initFramebuffer(width, height);

    clearScene1.setFramebuffer(clearFrameBuffer1);
    clearScene2.setFramebuffer(clearFrameBuffer2);
    workspaceScene.setFramebuffer(workspaceFrameBuffer);
    objectIDScene.setFramebuffer(objectIDFrameBuffer);
    generalPurposeScene.setFramebuffer(generalPurposeFrameBuffer);
    surfelsDepthScene.setFramebuffer(surfelsDepthFrameBuffer);
    surfelsAdditiveScene.setFramebuffer(surfelsAdditiveFrameBuffer);
    surfelsNormalizationScene.setFramebuffer(resolveFrameBuffer);
    postBlendingScene.setFramebuffer(postBlendingFrameBuffer);
    postBlendingCopyScene.setFramebuffer(resolveFrameBuffer);
    cadsAdditionalScene.setFramebuffer(cadsAdditionalFrameBuffer);

    const normalizationScreenPlane = surfelsNormalizationScene.getChildren()[0];
    const additiveTexture = surfelsAdditiveScene.getFramebuffer().colorAttachments[0];
    normalizationScreenPlane.setTexture(additiveTexture, 0);

    const copyScreenPlane = postBlendingCopyScene.getChildren()[0];
    const blendingTexture = postBlendingScene.getFramebuffer().colorAttachments[0];
    copyScreenPlane.setTexture(blendingTexture, 0);
  }

  initFramebuffer(width, height) {
    const blendTexturesType = compat.floatingTexturesRenderingEnabled ? pixelBufferType.T_RGBA16F : pixelBufferType.T_RGBA;

    const commonTextures = [];
    const clearSet1 = [];
    const clearSet2 = [];
    let depthBuffer = null;

    let mainColorTexture = null;
    let surfelsColorTexture = null;
    let blendingTexture = null;
    if (this.enableColorTexture) {
      const usedTexture = this.attachmentsStorage[attachmentId.BASE_COLOR_TEXTURE];
      const texture = usedTexture ? usedTexture : resourceManager.initBuffer(pixelBufferType.T_RGBA,
        {width, height});
      this.attachmentsStorage[attachmentId.BASE_COLOR_TEXTURE] = texture;

      mainColorTexture = texture;
      surfelsColorTexture = resourceManager.initBuffer(blendTexturesType,
        {width, height});
      blendingTexture = resourceManager.initBuffer(pixelBufferType.T_RGBA,
        {width, height});

      clearSet2.push(surfelsColorTexture);
      clearSet1.push(blendingTexture);
    }

    if (this.enableTriangleIDTexture) {
      const usedTexture = this.attachmentsStorage[attachmentId.TRIANGLE_ID_TEXTURE];
      const texture = usedTexture ? usedTexture : resourceManager.initBuffer(pixelBufferType.T_RGBA,
        {width, height});
      commonTextures.push(texture);
      this.attachmentsStorage[attachmentId.TRIANGLE_ID_TEXTURE] = texture;
      clearSet1.push(texture);
    }

    let objectIDTexture = null;
    if (this.enableObjectIDTexture) {
      const usedTexture = this.attachmentsStorage[attachmentId.OBJECT_ID_TEXTURE];
      objectIDTexture = usedTexture ? usedTexture : resourceManager.initBuffer(pixelBufferType.T_RGBA,
        {width, height});
      this.attachmentsStorage[attachmentId.OBJECT_ID_TEXTURE] = objectIDTexture;
      commonTextures.push(objectIDTexture);
      clearSet1.push(objectIDTexture);
    }

    let generalPurposeTexture = null;
    if (this.enableGeneralPurposeTexture) {
      generalPurposeTexture = resourceManager.initBuffer(pixelBufferType.T_RGBA,
        {width, height});
      commonTextures.push(generalPurposeTexture);
      clearSet1.push(generalPurposeTexture);
    }

    const areAnyColorAttachments = this.enableColorTexture || this.enableTriangleIDTexture || this.enableObjectIDTexture;
    if (areAnyColorAttachments) {
      const usedBuffer = this.attachmentsStorage[attachmentId.WORKSPACE_DEPTH_STENCIL];
      const format = GL.checkWebGL2() ? RBIntFormat.D24S8 : RBIntFormat.DS;
      const bufferType = config.recalculateInterfaceDepth ? pixelBufferType.T_DEPTH : pixelBufferType.RB_DEPTH;
      depthBuffer = usedBuffer ? usedBuffer : resourceManager.initBuffer(bufferType,
        { width, height, format });
      this.attachmentsStorage[attachmentId.WORKSPACE_DEPTH_STENCIL] = depthBuffer;
      if (!config.recalculateInterfaceDepth) {
        this.attachmentsStorage[attachmentId.INTERFACE_DEPTH] = depthBuffer;
      }

    }
    const dummyColorTexture = resourceManager.initBuffer(pixelBufferType.T_RGBA,{ width, height });
    const dummyCommonTextures = [];
    for (let i=0; i<commonTextures.length; i++) {
      dummyCommonTextures.push(resourceManager.initBuffer(blendTexturesType,{ width, height }));
    }

    const clearScene1Attachments = clearSet1.length ? clearSet1 : null;
    const clearScene2Attachments = clearSet2.length ? clearSet2 : null;
    const mainSceneColorAttachments = areAnyColorAttachments ? [mainColorTexture].concat(commonTextures) : null;
    const depthSceneColorAttachments = areAnyColorAttachments ? [dummyColorTexture].concat(commonTextures) : null;
    const additiveSceneColorAttachments = areAnyColorAttachments ? [surfelsColorTexture].concat(dummyCommonTextures) : null;
    const postBlendingSceneColorAttachments = areAnyColorAttachments ? [blendingTexture].concat(commonTextures) : null;
    const cadsAdditionalSceneColorAttachments = [];
    if (this.enableColorTexture) cadsAdditionalSceneColorAttachments.push(dummyColorTexture);
    if (this.enableObjectIDTexture) cadsAdditionalSceneColorAttachments.push(objectIDTexture);
    const cadsAdditionalSceneDepth = resourceManager.initBuffer(pixelBufferType.RB_DEPTH, { width, height,
      format: GL.checkWebGL2() ? RBIntFormat.D24S8 : RBIntFormat.DS });

    const clearFrameBuffer1 = new FrameBuffer(clearScene1Attachments, null);
    const clearFrameBuffer2 = new FrameBuffer(clearScene2Attachments, null);
    const workspaceFrameBuffer = new FrameBuffer(mainSceneColorAttachments, depthBuffer);
    const surfelsDepthFrameBuffer = new FrameBuffer(depthSceneColorAttachments, depthBuffer);
    const surfelsAdditiveFrameBuffer = new FrameBuffer(additiveSceneColorAttachments, depthBuffer);
    const postBlendingFrameBuffer = new FrameBuffer(postBlendingSceneColorAttachments, depthBuffer);
    const cadsAdditionalFrameBuffer = new FrameBuffer(cadsAdditionalSceneColorAttachments, cadsAdditionalSceneDepth);
    const resolveFrameBuffer = new FrameBuffer([mainColorTexture], null);

    const objectIDFrameBuffer = new FrameBuffer(objectIDTexture ? [objectIDTexture] : null, null);
    const generalPurposeFrameBuffer = new FrameBuffer(generalPurposeTexture ? [generalPurposeTexture] : null, null);

    return { clearFrameBuffer1, clearFrameBuffer2, workspaceFrameBuffer, objectIDFrameBuffer, generalPurposeFrameBuffer,
      surfelsDepthFrameBuffer, postBlendingFrameBuffer, resolveFrameBuffer, surfelsAdditiveFrameBuffer, cadsAdditionalFrameBuffer };

  }

  //reimplementation
  getMainScene() {
    return this.getScenes()[2];
  }

  getPostBlendingScene() {
    return this.getScenes()[6];
  }

}
